import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { FreeModeProvider } from "@/hooks/useFreeMode";
import { AnalyticsProvider } from "@/hooks/useAnalytics";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load all non-critical route components
const Tools = lazy(() => import("./pages/Tools"));
const About = lazy(() => import("./pages/About"));
const Legal = lazy(() => import("./pages/Legal"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const KnowledgeLearning = lazy(() => import("./pages/KnowledgeLearning"));
const LearningPathDetail = lazy(() => import("./pages/LearningPathDetail"));
const KnowledgeResearch = lazy(() => import("./pages/KnowledgeResearch"));
const KnowledgeNews = lazy(() => import("./pages/KnowledgeNews"));
const KnowledgeArticle = lazy(() => import("./pages/KnowledgeArticle"));
const KnowledgeSection = lazy(() => import("./pages/KnowledgeSection"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const History = lazy(() => import("./pages/History"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Awareness = lazy(() => import("./pages/Awareness"));
const AwarenessFundamentals = lazy(() => import("./pages/AwarenessFundamentals"));
const AwarenessFundamentalDetail = lazy(() => import("./pages/AwarenessFundamentalDetail"));
const AwarenessDictionary = lazy(() => import("./pages/AwarenessDictionary"));
const AwarenessStories = lazy(() => import("./pages/AwarenessStories"));
const AwarenessStoryDetail = lazy(() => import("./pages/AwarenessStoryDetail"));
const AwarenessFraudTypes = lazy(() => import("./pages/AwarenessFraudTypes"));
const AwarenessFraudDetail = lazy(() => import("./pages/AwarenessFraudDetail"));
const AwarenessProtection = lazy(() => import("./pages/AwarenessProtection"));
const AwarenessProtectionDetail = lazy(() => import("./pages/AwarenessProtectionDetail"));
const AwarenessEvaluations = lazy(() => import("./pages/AwarenessEvaluations"));
const AwarenessEvaluationTake = lazy(() => import("./pages/AwarenessEvaluationTake"));
const AwarenessPhishingSimulator = lazy(() => import("./pages/AwarenessPhishingSimulator"));
const AwarenessWebSimulator = lazy(() => import("./pages/AwarenessWebSimulator"));
const AwarenessMessageSimulator = lazy(() => import("./pages/AwarenessMessageSimulator"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <FreeModeProvider>
            <AnalyticsProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/tools" element={<Tools />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/knowledge" element={<Knowledge />} />
                  <Route path="/knowledge/learning" element={<KnowledgeLearning />} />
                  <Route path="/knowledge/learning/path/:slug" element={<LearningPathDetail />} />
                  <Route path="/knowledge/research" element={<KnowledgeResearch />} />
                  <Route path="/knowledge/news" element={<KnowledgeNews />} />
                  <Route path="/knowledge/article/:slug" element={<KnowledgeArticle />} />
                  <Route path="/knowledge/section/:slug" element={<KnowledgeSection />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/admin" element={<Admin />} />
                  {/* Conciencia Digital */}
                  <Route path="/conciencia" element={<Awareness />} />
                  <Route path="/conciencia/fundamentos" element={<AwarenessFundamentals />} />
                  <Route path="/conciencia/fundamentos/:slug" element={<AwarenessFundamentalDetail />} />
                  <Route path="/conciencia/diccionario" element={<AwarenessDictionary />} />
                  <Route path="/conciencia/historias" element={<AwarenessStories />} />
                  <Route path="/conciencia/historias/:slug" element={<AwarenessStoryDetail />} />
                  <Route path="/conciencia/fraudes" element={<AwarenessFraudTypes />} />
                  <Route path="/conciencia/fraudes/:slug" element={<AwarenessFraudDetail />} />
                  <Route path="/conciencia/proteccion" element={<AwarenessProtection />} />
                  <Route path="/conciencia/proteccion/:slug" element={<AwarenessProtectionDetail />} />
                  <Route path="/conciencia/evaluaciones" element={<AwarenessEvaluations />} />
                  <Route path="/conciencia/evaluaciones/:slug" element={<AwarenessEvaluationTake />} />
                  <Route path="/conciencia/simulador-phishing" element={<AwarenessPhishingSimulator />} />
                  <Route path="/conciencia/simulador-webs" element={<AwarenessWebSimulator />} />
                  <Route path="/conciencia/simulador-mensajes" element={<AwarenessMessageSimulator />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AnalyticsProvider>
          </FreeModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
